"use client";
import { RefObject, useSyncExternalStore } from "react";

// Scroll fires far more often than the screen repaints, and every notification
// makes React re-read `offsetHeight` + `getBoundingClientRect()` below — a
// forced reflow each time. Coalescing into one frame keeps the value identical
// (the snapshot is read fresh) while costing one layout per paint instead of
// one per event.
const subscribe = (onStoreChange: () => void) => {
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      onStoreChange();
    });
  };

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  return () => {
    cancelAnimationFrame(frame);
    window.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
  };
};

/**
 * How far a taller-than-viewport section has been scrolled through, 0 → 1.
 *
 * Read during render rather than mirrored into state: the value is derived from
 * the DOM, so an effect would only add a render behind the scroll. `enabled` is
 * false below lg, where every section falls back to a plain stacked layout.
 */
export const useSectionProgress = (
  sectionRef: RefObject<HTMLElement | null>,
  enabled: boolean = true,
) =>
  useSyncExternalStore(
    subscribe,
    () => {
      const section = sectionRef.current;
      if (!enabled || !section) return 0;
      const total = section.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      const scrolled = -section.getBoundingClientRect().top / total;
      return Math.min(1, Math.max(0, scrolled));
    },
    () => 0,
  );
